import { useState } from 'react';
import {
  Alert,
  Button,
  Field,
  Input,
  Spinner,
  Stack,
  Text,
} from '@billda/design-system';
import { FormProvider } from 'react-hook-form';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { useSiteDetails } from '../../../hooks/useSiteDetails';
import { useAddPlotForm } from '../../../hooks/useAddPlotForm';
import { ConfirmAddPlotDialog } from '../../molecules/ConfirmAddPlotDialog';
import type { AddPlotFormData } from '../../../validation/addPlotSchema';

interface AddPlotPanelProps {
  siteId: number;
}

/**
 * Panel for adding plots to a site
 * Organism component following Atomic Design Structure
 * Follows the form architecture pattern with 3-layer structure
 */
export function AddPlotPanel({ siteId }: AddPlotPanelProps) {
  const { closePanel } = useSitePanelState();
  const { data: site } = useSiteDetails(siteId);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<AddPlotFormData | null>(null);

  const { form, submitData, isSubmitting, successMessage, errorMessage } =
    useAddPlotForm(siteId, {
      onSuccess: () => {
        // Close panel after successful submission
        closePanel();
      },
    });

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = form;

  // Handle form submission - show confirmation dialog
  const onSubmit = async (data: AddPlotFormData) => {
    setPendingData(data);
    setShowConfirm(true);
  };

  // Handle confirmation - actually submit the form
  const handleConfirm = async () => {
    setShowConfirm(false);
    if (pendingData) {
      await submitData(pendingData);
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingData(null);
  };

  // No site data (initial loading or truly no data)
  if (!site) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      {/* Success/Error messages */}
      {successMessage && (
        <Alert severity="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" className="mb-4">
          {errorMessage}
        </Alert>
      )}

      {/* Info banner */}
      <Alert severity="info" className="mb-6">
        Create a new empty plot for this site. You can add labour items to it
        after creation.
      </Alert>

      {/* Form */}
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-1 flex-col"
        >
          <Stack spacing={3} className="flex-1">
            {/* Plot Name */}
            <Field label="Plot Name *" error={errors.name?.message?.toString()}>
              <Input
                invalid={!!errors.name}
                autoFocus
                placeholder="e.g., Plot A1"
                {...register('name')}
              />
            </Field>

            {/* Plot Number */}
            <Field
              label="Plot Number *"
              error={errors.plotNumber?.message?.toString()}
            >
              <Input
                invalid={!!errors.plotNumber}
                placeholder="e.g., A1-001"
                {...register('plotNumber')}
              />
            </Field>
          </Stack>

          {/* Site Information */}
          <div className="mt-4 rounded-lg bg-surface-raised p-4">
            <Text variant="body2" color="secondary">
              Adding to site: <strong>{site.name}</strong>
            </Text>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-4">
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              fullWidth
            >
              Create Plot
            </Button>
            <Button
              variant="outline"
              onClick={() => closePanel()}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </form>
      </FormProvider>

      {/* Confirmation Dialog */}
      <ConfirmAddPlotDialog
        open={showConfirm}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        plotName={pendingData?.name}
        plotNumber={pendingData?.plotNumber}
        siteName={site?.name}
      />
    </div>
  );
}
